import { getDeterministicAvatarColors } from "@mg/schemas/src/commons/avatarColors";

/**
 * Loose shape of any user-like object that can feed an initials avatar. We
 * intentionally accept the union of identity keys (`_id`, `userID`, `id`,
 * `email`) because the different payloads that render avatars name the user id
 * field differently:
 *  - REST/Mongo user docs and comment authors use `_id`.
 *  - The decoded auth JWT (`state.auth.value`) uses `userID`.
 *  - Some list rows only carry `email` / `name`.
 */
export interface AvatarColorUser {
  _id?: string | null;
  userID?: string | null;
  id?: string | null;
  email?: string | null;
  name?: string | null;
  avatarBackgroundColor?: string | null;
  avatarTextColor?: string | null;
}

export interface ResolvedAvatarColors {
  /** Concrete, ready-to-apply background color (hex). Never empty. */
  backgroundColor: string;
  /** Concrete, ready-to-apply text color (hex). Never empty. */
  textColor: string;
  /** The stable identity key the deterministic default was derived from. */
  colorKey: string;
}

/**
 * Single source of truth for an initials-avatar's colors. Explicit persisted
 * colors the user picked in Edit Profile win; otherwise we fall back to a
 * STABLE deterministic default keyed on the user's identity (id/email), NOT the
 * display name — the name varies per pane and changes while typing, which is
 * exactly what made the placeholder color appear to "keep changing".
 *
 * Returns concrete colors (never undefined) so non-`AvatarWithInitials`
 * renderers (e.g. the on-canvas comment pin) can apply them directly, while
 * `AvatarWithInitials` call sites can spread the same values and stay
 * consistent with the Edit Profile preview.
 */
export function resolveAvatarColors(
  user?: AvatarColorUser | null,
): ResolvedAvatarColors {
  const colorKey =
    user?._id || user?.userID || user?.id || user?.email || user?.name || "";

  const defaults = getDeterministicAvatarColors(colorKey);

  return {
    backgroundColor: user?.avatarBackgroundColor ?? defaults.backgroundColor,
    textColor: user?.avatarTextColor ?? defaults.textColor,
    colorKey,
  };
}
